import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import providerLogo from '../../assets/images/logos/lcs_logo.png';
import HeaderContainer from '../../components/layout/HeaderContainer';
import BrandRow from '../../components/layout/BrandRow';
import FormHeader from '../../components/layout/FormHeader';
import SubmitBar from '../../components/SubmitBar';
import useLayoutMetrics from '../../hooks/useLayoutMetrics';

/**
 * EmailAlreadyInUsePage
 * ---------------------
 * This page is displayed when a user tries to start a new referral with an email
 * address that is already associated with an existing account.
 * 
 * Purpose:
 *   Instead of using a browser dialog (window.confirm), we provide a proper page
 *   experience that seamlessly integrates with the form wizard flow:
 *   1. Clearly explains the situation (email already registered)
 *   2. Offers two clear navigation options via the same SubmitBar used in forms:
 *      - Back: "Go back and change email" → returns to the referral form and clears email fields
 *      - Next: "Go to Sign In" → navigates to the sign-in page
 * 
 * UX Rationale:
 *   - Browser dialogs are jarring and don't match the polished form experience
 *   - Using the same layout and SubmitBar as the main form makes this feel like
 *     a natural step in the wizard rather than an interruption
 *   - Users can take their time to decide without feeling pressured by a modal
 * 
 * Query Parameters:
 *   - email: The email address that was found to be already in use (for display)
 *   - returnTo: The URL to return to when "Go back and change email" is clicked
 *     (typically includes intentId to resume the form at the right step)
 */
export default function EmailAlreadyInUsePage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use the same layout metrics hook as the main form for consistent spacing
  const { rootRef, headerRef } = useLayoutMetrics();
  
  // Extract query parameters
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const email = params.get('email') || 'your email';
  const returnTo = params.get('returnTo') || '/rms/referral/init';

  /**
   * Handle "Go back and change email" button click (Back button).
   * Navigates back to the referral form with a special flag indicating that
   * email fields should be cleared. The referral form reads this flag and
   * clears the ref_email and ref_email_confirm fields.
   */
  const handleGoBack = () => {
    // Append a clearEmail flag to the returnTo URL so the form knows to clear email fields
    const separator = returnTo.includes('?') ? '&' : '?';
    navigate(`${returnTo}${separator}clearEmail=true`);
  };

  /**
   * Handle "Go to Sign In" button click (Next/forward button).
   * Navigates to the sign-in page where the user can log in to their existing account.
   */
  const handleGoToSignIn = () => {
    navigate('/rms/sign-in');
  };

  return (
    <div 
      ref={rootRef}
      className="rms-form-root min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col"
    >
      {/* Header - matches the main form layout exactly */}
      <HeaderContainer headerRef={headerRef}>
        <BrandRow logoSrc={providerLogo} alt="LCS logo" />
        <FormHeader
          formTitle="Referral Form"
          headingOverride="Email Already Registered"
        />
      </HeaderContainer>

      {/* Main content area - matches the form layout structure */}
      <div className="flex-grow w-full px-6 md:px-10 text-left">
        <div className="w-full md:w-1/2 mx-auto">
          {/* Top gap using CSS variable populated by useLayoutMetrics */}
          <div style={{ height: 'var(--rms-question-top-gap)' }} />
          
          {/* Content - styled like a question card */}
          <div className="space-y-4">
            {/* Question-style title */}
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">
              This email is already in use
            </h2>
            
            {/* Explanation text */}
            <div className="space-y-4 text-gray-700">
              <p>
                The email address <span className="font-semibold text-gray-900">{email}</span> is already associated with an existing account.
              </p>
              <p>
                If you've made a referral before using this email, please sign in to your account to make new referrals and manage your existing ones.
              </p>
              <p>
                Alternatively, you can go back and use a different email address to continue as a new user.
              </p>
            </div>
          </div>

          {/* Navigation buttons - using SubmitBar for consistent styling */}
          <div className="mt-6">
            <SubmitBar
              onBack={handleGoBack}
              onNext={handleGoToSignIn}
              backLabel="Go back and change email"
              nextLabel="Go to Sign In"
              hideBack={false}
              disabled={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
